import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'deliver-files-with-telegram-stars',
  title: 'Deliver a File Automatically After a Telegram Stars Payment',
  metaTitle: 'Deliver Files After a Telegram Stars Payment',
  metaDescription:
    'Sell an ebook, video, audio, photo, HTTPS link or message with Telegram Stars. The file is sent after payment, up to 20 MB.',
  keywords: [
    'sell ebook telegram stars',
    'telegram stars digital delivery',
    'send file after telegram payment',
    'telegram stars content sale',
    'telegram paid download',
    'automatic delivery telegram stars',
  ],
  category: 'Guides',
  datePublished: '2026-10-23',
  dateModified: '2026-10-23',
  readingTimeMin: 7,
  excerpt:
    'A content sale sends the buyer the file, link or message as soon as Telegram confirms the Stars payment. You upload it once, share one link, and stop answering “where is my download?” by hand.',
  keyTakeaways: [
    'A content invoice delivers a file, photo, video, audio, HTTPS link or text message in Telegram after each confirmed payment.',
    'Uploads are limited to 20 MB. Larger courses belong on an HTTPS link, not inside the file upload.',
    'Turn on “close after the first purchase” when the file is personal. Leave it off for a product many people can buy.',
    'Private channel membership is not a content sale. That requires a subscription invoice.',
  ],
  howTo: {
    name: 'Sell a file with Telegram Stars',
    steps: [
      { name: 'Link Telegram', text: 'In settings, connect your Telegram account and start the bot so uploads can be stored.' },
      { name: 'Create a content invoice', text: 'Choose Content sale, set the Stars price, title and description.' },
      { name: 'Upload or paste the delivery', text: 'Upload a file up to 20 MB, or paste an HTTPS link or the message to send.' },
      { name: 'Choose reuse', text: 'Leave the link open for many buyers, or close it after the first purchase.' },
      { name: 'Share the link', text: 'Post it. After payment the buyer receives the content in Telegram.' },
    ],
  },
  body: [
    h2('What gets delivered'),
    p(
      'A **content sale** is a payment link plus a payload. Telegram Stars Payment Platform sends that payload in the buyer’s chat with the bot once the payment is confirmed. You choose one of these:',
    ),
    table(
      ['Delivery', 'Use it for', 'Limit'],
      [
        ['File / ebook', 'PDF, zip, template', '20 MB'],
        ['Photo', 'A pack of images', '20 MB'],
        ['Video', 'A short lesson', '20 MB'],
        ['Audio', 'A voice note or track', '20 MB'],
        ['HTTPS link', 'A course host, Notion, Drive', 'HTTPS only'],
        ['Message', 'A newsletter issue, a code, a thank-you', 'About 4,000 characters'],
      ],
    ),
    p('An optional delivery message can sit next to the file, for example “Your login is in the PDF, page 2.”'),

    h2('Set it up'),
    ol([
      'Open **Settings**, link your Telegram account and start the bot. The upload is also sent into your conversation with the bot, which is how you confirm the file is the right one.',
      '[Create an invoice](/login) and choose **Content sale**.',
      'Set the title, description and amount in Stars. The title is what the buyer sees on the payment sheet, so name the product, not the file.',
      'Upload the file or paste the HTTPS link. Wait until the file shows as ready before you create the invoice.',
      'Leave **Close after the first purchase** off for a product you will sell many times. Turn it on for a custom file you will only send once.',
    ]),
    callout(
      'Links must be HTTPS. A `http://` URL is rejected. If the file is over 20 MB, host it behind an HTTPS link and deliver the link, not the file.',
    ),

    h2('One buyer or many'),
    p(
      'A normal content link stays open. Every payment sends the same file and credits your balance again. That is the right setup for an ebook, a preset pack or a replay.',
    ),
    p(
      '**Close after the first purchase** makes the link single-use. The second person who opens it cannot pay. Use that for a commissioned file, a private audit, or a license you only want to sell once. For a coaching call that is not a file, a single payment (no content payload) is enough; see [Telegram Stars for freelancers](/blog/telegram-stars-for-freelancers).',
    ),

    h2('What this is not'),
    ul([
      '**Not a private channel.** Channel IDs are refused on a content sale. Use a [subscription](/blog/telegram-stars-subscriptions) if the product is ongoing access.',
      '**Not a card checkout.** The buyer pays in Stars. If they are short, Telegram offers a top-up inside the sheet, then completes the purchase.',
      '**Not instant cash.** Stars are credited immediately in your ledger and become withdrawable after 21 days. The fee on Telegram Stars Payment Platform is 3%.',
    ]),

    h2('Make the handoff obvious'),
    ul([
      'The description should say where the file arrives: “Sent in this chat right after payment.”',
      'Name the format: “PDF, 42 pages” beats “digital product”.',
      'If you deliver a link, say whether it expires. A dead Drive link is the usual reason people ask for a refund.',
      'For a course with several files, zip them if the zip stays under 20 MB, or deliver one HTTPS page that lists every file.',
    ]),
    p('More product ideas are in [12 ways to sell digital products with Stars](/blog/sell-digital-products-telegram-stars).'),
  ],
  faq: [
    {
      q: 'Does the buyer need to message the bot before paying?',
      a: 'They open the payment link in Telegram and confirm. The file is sent in the bot chat after payment, so they should not block the bot.',
    },
    {
      q: 'Can I update the file later?',
      a: 'Create a new content invoice with the new file and point your redirect slug at it. People who already paid keep what they received; the next buyer gets the new file.',
    },
    {
      q: 'Why was my link rejected?',
      a: 'Delivery links must be HTTPS. Check the URL starts with https:// and that the page is reachable without a login wall if you promised instant access.',
    },
    {
      q: 'Can I sell the same ebook to a hundred people?',
      a: 'Yes. Leave “close after the first purchase” off. Each payment delivers the same file and adds a ledger entry.',
    },
  ],
  relatedSlugs: [
    'sell-digital-products-telegram-stars',
    'telegram-stars-payment-links',
    'telegram-stars-subscriptions',
    'telegram-stars-for-freelancers',
  ],
};

export default article;
